/**
 * Join Project P&L / QB expenses to issued POs.
 * Stored card + bill links win; unique vendor/amount/date leftovers are heuristic.
 */

import {
  amountsMatch,
  datesWithinMatchWindow,
  linkedQbExpenseIds,
  vendorsOverlap,
  type CardPoMatchExpense,
  type CardPoMatchRequisition,
} from './cardExpensePurchaseMatch';

export type ProjectPlPoMatchSource = 'expense' | 'bill' | 'heuristic' | 'none';

export type ProjectPlSplitSlice = {
  dealId: string | null;
  amount?: number;
};

export type ProjectPlMatchExpense = CardPoMatchExpense & {
  id: string;
  quickbooksId?: string | null;
  entityType?: string | null;
  poNumber?: string | null;
  matchedDealId?: string | null;
  splits?: ProjectPlSplitSlice[] | null;
};

export type ProjectPlMatchPo = CardPoMatchRequisition & {
  qbBillStatus?: string | null;
  qbBillId?: string | null;
  designerInvoiceId?: string | null;
};

export type ProjectPlExpenseMatch = {
  poId: string;
  poNumber: string;
  source: Exclude<ProjectPlPoMatchSource, 'none'>;
};

export type ProjectPlPoMatch = {
  expenseIds: string[];
  source: ProjectPlPoMatchSource;
  qbBillStatus?: string | null;
};

export type ProjectPlPoQbJoin = {
  expenseById: Record<string, ProjectPlExpenseMatch>;
  poById: Record<string, ProjectPlPoMatch>;
};

function poLabel(po: ProjectPlMatchPo): string {
  return (po.poNumber || po.prNumber || po.id).trim();
}

function emptyPoMatch(po: ProjectPlMatchPo): ProjectPlPoMatch {
  return {
    expenseIds: [],
    source: 'none',
    qbBillStatus: po.qbBillStatus || undefined,
  };
}

function addExpenseToPo(
  poById: Record<string, ProjectPlPoMatch>,
  po: ProjectPlMatchPo,
  expenseId: string,
  source: Exclude<ProjectPlPoMatchSource, 'none'>,
): void {
  const cur = poById[po.id] || emptyPoMatch(po);
  if (!cur.expenseIds.includes(expenseId)) cur.expenseIds.push(expenseId);
  if (cur.source === 'none' || (cur.source === 'heuristic' && source !== 'heuristic')) {
    cur.source = source;
  } else if (source === 'bill' && cur.source === 'expense') {
    cur.source = 'bill';
  }
  poById[po.id] = cur;
}

function vendorHaystack(expense: ProjectPlMatchExpense): string {
  return [expense.vendorName, expense.description].filter(Boolean).join(' ');
}

function poHaystack(po: ProjectPlMatchPo): string {
  return [po.vendorName, po.title].filter(Boolean).join(' ');
}

function poDealId(po: ProjectPlMatchPo): string {
  return (po.dealId || po.costObject?.dealId || '').trim();
}

export function expenseAmountForDeal(expense: ProjectPlMatchExpense, dealId: string): number {
  const id = dealId.trim();
  if (!id) return 0;
  const splits = expense.splits || [];
  if (splits.length) {
    return splits
      .filter((slice) => (slice.dealId || '').trim() === id)
      .reduce((sum, slice) => sum + (Number(slice.amount) || 0), 0);
  }
  if ((expense.matchedDealId || '').trim() === id) {
    return Number(expense.totalAmount) || 0;
  }
  return 0;
}

export function dealMatchedQbBillCoversPo(expense: ProjectPlMatchExpense, po: ProjectPlMatchPo): boolean {
  if ((expense.entityType || 'bill') !== 'bill') return false;
  const dealId = poDealId(po);
  if (!dealId) return false;
  const alloc = expenseAmountForDeal(expense, dealId);
  if (alloc <= 0) return false;
  if (!vendorsOverlap(vendorHaystack(expense), poHaystack(po))) return false;
  if (amountsMatch(alloc, po)) return true;
  return amountsMatch(Number(expense.totalAmount) || 0, po);
}

function heuristicFits(expense: ProjectPlMatchExpense, po: ProjectPlMatchPo): boolean {
  if (!vendorsOverlap(vendorHaystack(expense), poHaystack(po))) return false;
  if (!amountsMatch(Number(expense.totalAmount) || 0, po)) return false;
  return datesWithinMatchWindow(expense, po);
}

/** First-win join: stored card, stored bill, deal-matched QB bill, then unique leftover heuristic. */
export function joinProjectPlExpensesToPos(
  expenses: ProjectPlMatchExpense[],
  pos: ProjectPlMatchPo[],
): ProjectPlPoQbJoin {
  const expenseById: Record<string, ProjectPlExpenseMatch> = {};
  const poById: Record<string, ProjectPlPoMatch> = {};
  const poMap = new Map(pos.map(po => [po.id, po]));

  for (const po of pos) poById[po.id] = emptyPoMatch(po);

  const setLink = (
    expense: ProjectPlMatchExpense,
    po: ProjectPlMatchPo,
    source: Exclude<ProjectPlPoMatchSource, 'none'>,
    opts?: { allowSharedBill?: boolean },
  ) => {
    const existing = expenseById[expense.id];
    if (existing && existing.poId !== po.id && !opts?.allowSharedBill) return;
    if (!existing) {
      expenseById[expense.id] = { poId: po.id, poNumber: poLabel(po), source };
    }
    addExpenseToPo(poById, po, expense.id, source);
  };

  for (const expense of expenses) {
    const storedId = (expense.purchaseRequisitionId || '').trim();
    if (!storedId) continue;
    const po = poMap.get(storedId);
    if (po) setLink(expense, po, 'expense');
  }

  for (const po of pos) {
    for (const expenseId of linkedQbExpenseIds(po)) {
      const expense = expenses.find(row => row.id === expenseId);
      if (expense) setLink(expense, po, 'expense');
    }
  }

  for (const expense of expenses) {
    const qbId = (expense.quickbooksId || '').trim();
    if (!qbId) continue;
    const hits = pos.filter(po => (po.qbBillId || '').trim() === qbId);
    if (hits.length >= 1) {
      hits.forEach(po => setLink(expense, po, 'bill', { allowSharedBill: true }));
      continue;
    }
    const linkedStatusHits = pos.filter(
      po => (po.qbBillStatus || '').trim() === 'linked' && (po.qbBillId || '').trim() === qbId,
    );
    if (linkedStatusHits.length === 1) setLink(expense, linkedStatusHits[0], 'bill');
  }

  const leftoverPos = () => pos.filter(po => (poById[po.id]?.expenseIds.length || 0) === 0);
  const leftoverExpenses = expenses
    .filter(expense => !expenseById[expense.id])
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id));

  for (const expense of leftoverExpenses) {
    const dealHits = leftoverPos().filter(po => dealMatchedQbBillCoversPo(expense, po));
    if (dealHits.length >= 1) {
      dealHits.forEach(po => setLink(expense, po, 'bill', { allowSharedBill: dealHits.length > 1 }));
      continue;
    }
    const hits = leftoverPos().filter(po => heuristicFits(expense, po));
    if (hits.length !== 1) continue;
    setLink(expense, hits[0], 'heuristic');
  }

  return { expenseById, poById };
}

export function heuristicAscriptions(
  join: ProjectPlPoQbJoin,
): Array<{ expenseId: string; poId: string; poNumber: string }> {
  return Object.entries(join.expenseById)
    .filter(([, match]) => match.source === 'heuristic')
    .map(([expenseId, match]) => ({
      expenseId,
      poId: match.poId,
      poNumber: match.poNumber,
    }))
    .sort((a, b) => a.expenseId.localeCompare(b.expenseId));
}

export function poQbStatusLabel(input: {
  source: ProjectPlPoMatchSource;
  qbBillStatus?: string | null;
}): { qbStatus: 'linked_expense' | 'linked_bill' | 'pending_bill' | 'none'; qbLabel: string } {
  if (input.source === 'bill' || (input.source !== 'none' && input.qbBillStatus === 'linked')) {
    return { qbStatus: 'linked_bill', qbLabel: 'QB bill' };
  }
  if (input.source === 'expense' || input.source === 'heuristic') {
    return { qbStatus: 'linked_expense', qbLabel: 'QB charge' };
  }
  if (input.qbBillStatus === 'needs_vendor') {
    return { qbStatus: 'pending_bill', qbLabel: 'QB vendor needed' };
  }
  if (input.qbBillStatus === 'needs_bill') {
    return { qbStatus: 'pending_bill', qbLabel: 'Not in QB' };
  }
  if (input.qbBillStatus === 'pending') {
    return { qbStatus: 'pending_bill', qbLabel: 'QB pending' };
  }
  return { qbStatus: 'none', qbLabel: 'No QB' };
}
